import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SecurityPostureScanner = () => {
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [viewMode, setViewMode] = useState('form');
  const [scans, setScans] = useState([]);
  
  // SuperAdmin: selector de empresa
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Manual validation state
  const [manualValidation, setManualValidation] = useState({
    antivirus_manually_confirmed: false,
    antivirus_manual_details: '',
    firewall_manually_confirmed: false,
    firewall_manual_details: '',
    vpn_manually_confirmed: false,
    vpn_manual_details: '',
    auditor_notes: ''
  });
  const [savingValidation, setSavingValidation] = useState(false);

  useEffect(() => {
    // Obtener info del usuario actual
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          
          // Si es SuperAdmin, cargar lista de empresas
          if (response.data.role === 'superadmin') {
            fetchCompanies();
          } else {
            // Usuario normal: cargar sus datos directamente
            fetchTargets();
            fetchScans();
            fetchManualValidation();
          }
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  // Cuando SuperAdmin selecciona una empresa, cargar sus datos
  useEffect(() => {
    if (user?.role === 'superadmin' && selectedCompanyId) {
      fetchTargets(selectedCompanyId);
      fetchScans(selectedCompanyId);
      fetchManualValidation(selectedCompanyId);
    }
  }, [selectedCompanyId, user]);

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtrar la empresa del SuperAdmin
      const clientCompanies = response.data.filter(c => c.name !== 'SegurIA Platform');
      setCompanies(clientCompanies);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchTargets = async (companyId = null) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/targets`;
      if (companyId) {
        url += `?company_id=${companyId}`;
      }
      const response = await axios.get(url, { headers });
      setTargets(response.data);
    } catch (error) {
      console.error("Error fetching targets:", error);
    }
  };

  const fetchScans = async (companyId = null) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/security-posture/scans`;
      if (companyId) {
        url += `?company_id=${companyId}`;
      }
      const response = await axios.get(url, { headers });
      setScans(response.data);
    } catch (error) {
      console.error('Error fetching security posture scans:', error);
    }
  };

  const fetchManualValidation = async (companyId = null) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/security-posture/manual-validation`;
      if (companyId) {
        url += `?company_id=${companyId}`;
      }
      const response = await axios.get(url, { headers });
      setManualValidation(response.data);
    } catch (error) {
      console.error('Error fetching manual validation:', error);
    }
  };

  const saveManualValidation = async () => {
    if (user?.role !== 'superadmin') {
      alert('Solo SuperAdmin puede guardar validaciones manuales');
      return;
    }
    
    if (!selectedCompanyId) {
      alert('Debe seleccionar una empresa primero');
      return;
    }

    setSavingValidation(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        antivirus_confirmed: manualValidation.antivirus_manually_confirmed,
        antivirus_details: manualValidation.antivirus_manual_details || '',
        firewall_confirmed: manualValidation.firewall_manually_confirmed,
        firewall_details: manualValidation.firewall_manual_details || '',
        vpn_confirmed: manualValidation.vpn_manually_confirmed,
        vpn_details: manualValidation.vpn_manual_details || '',
        auditor_notes: manualValidation.auditor_notes || '',
        company_id: selectedCompanyId
      });

      await axios.post(`${API}/security-posture/manual-validation?${params.toString()}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('✓ Validaciones manuales guardadas correctamente');
    } catch (error) {
      alert('Error al guardar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingValidation(false);
    }
  };

  const startScan = async (e) => {
    e.preventDefault();
    if (!selectedTarget) {
      alert("Por favor selecciona un target");
      return;
    }

    setLoading(true);
    setViewMode('scanning');
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/security-posture/scan`, null, { headers,
        params: { target_id: selectedTarget }
      });
      
      const scanId = response.data.scan_id;
      pollScanStatus(scanId);
    } catch (error) {
      alert("Error iniciando escaneo: " + (error.response?.data?.detail || error.message));
      setLoading(false);
      setViewMode('form');
    }
  };

  const pollScanStatus = async (scanId) => {
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/scans/${scanId}`, { headers });
        const scan = response.data;
        
        setScanProgress(scan);
        
        if (scan.status === 'completed') {
          clearInterval(interval);
          setScanResults(scan.results);
          setLoading(false);
          setViewMode('results');
        } else if (scan.status === 'failed') {
          clearInterval(interval);
          alert("Error en el escaneo: " + (scan.error_message || 'Error desconocido'));
          setLoading(false);
          setViewMode('form');
        }
      } catch (error) {
        console.error("Error polling scan:", error);
      }
    }, 2000);
  };

  const getScoreColor = (score) => {
    if (score >= 40) return 'text-green-600';
    if (score >= 25) return 'text-yellow-600';
    if (score >= 15) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 40) return 'bg-green-100 border-green-500';
    if (score >= 25) return 'bg-yellow-100 border-yellow-500';
    if (score >= 15) return 'bg-orange-100 border-orange-500';
    return 'bg-red-100 border-red-500';
  };

  // Selector de empresa para SuperAdmin
  const renderCompanySelector = () => {
    if (user?.role !== 'superadmin') return null;

    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
          🔐 Modo SuperAdmin - Seleccionar Empresa
        </h3>
        {loadingCompanies ? (
          <p className="text-gray-600">Cargando empresas...</p>
        ) : (
          <div>
            <select
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white"
            >
              <option value="">-- Selecciona una empresa para auditar --</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name} ({company.subscription_plan})
                </option>
              ))}
            </select>
            {selectedCompanyId && (
              <p className="mt-2 text-sm text-red-600">
                ✓ Trabajando sobre: <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong>
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Panel de validación manual (solo SuperAdmin)
  const renderManualValidationPanel = () => {
    if (user?.role !== 'superadmin') return null;
    if (!selectedCompanyId) return null;

    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mt-6 border-2 border-purple-200">
        <h3 className="text-xl font-bold text-purple-800 mb-4 flex items-center">
          ✍️ Validación Manual de Postura de Seguridad
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Complete estos campos con la información proporcionada por el cliente. 
          Esta validación manual complementa la detección automática.
        </p>

        <div className="space-y-4">
          {/* Antivirus */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="av_confirmed"
                checked={manualValidation.antivirus_manually_confirmed}
                onChange={(e) => setManualValidation({
                  ...manualValidation,
                  antivirus_manually_confirmed: e.target.checked
                })}
                className="w-5 h-5 text-green-600 rounded"
              />
              <label htmlFor="av_confirmed" className="ml-2 font-medium text-gray-700">
                🛡️ Antivirus Enterprise Confirmado
              </label>
            </div>
            <input
              type="text"
              placeholder="Ej: Kaspersky Endpoint Security 12.0"
              value={manualValidation.antivirus_manual_details}
              onChange={(e) => setManualValidation({
                ...manualValidation,
                antivirus_manual_details: e.target.value
              })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>

          {/* Firewall */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="fw_confirmed"
                checked={manualValidation.firewall_manually_confirmed}
                onChange={(e) => setManualValidation({
                  ...manualValidation,
                  firewall_manually_confirmed: e.target.checked
                })}
                className="w-5 h-5 text-green-600 rounded"
              />
              <label htmlFor="fw_confirmed" className="ml-2 font-medium text-gray-700">
                🔥 Firewall Perimetral Confirmado
              </label>
            </div>
            <input
              type="text"
              placeholder="Ej: Fortinet FortiGate 100F"
              value={manualValidation.firewall_manual_details}
              onChange={(e) => setManualValidation({
                ...manualValidation,
                firewall_manual_details: e.target.value
              })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>

          {/* VPN */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="vpn_confirmed"
                checked={manualValidation.vpn_manually_confirmed}
                onChange={(e) => setManualValidation({
                  ...manualValidation,
                  vpn_manually_confirmed: e.target.checked
                })}
                className="w-5 h-5 text-green-600 rounded"
              />
              <label htmlFor="vpn_confirmed" className="ml-2 font-medium text-gray-700">
                🔐 VPN Corporativa Confirmada
              </label>
            </div>
            <input
              type="text"
              placeholder="Ej: Cisco AnyConnect, OpenVPN"
              value={manualValidation.vpn_manual_details}
              onChange={(e) => setManualValidation({
                ...manualValidation,
                vpn_manual_details: e.target.value
              })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>

          {/* Notas del auditor */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <label className="block font-medium text-gray-700 mb-2">
              📝 Notas del Auditor
            </label>
            <textarea
              placeholder="Observaciones adicionales sobre la postura de seguridad..."
              value={manualValidation.auditor_notes}
              onChange={(e) => setManualValidation({
                ...manualValidation,
                auditor_notes: e.target.value
              })}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>

          <button
            onClick={saveManualValidation}
            disabled={savingValidation}
            className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 font-semibold"
          >
            {savingValidation ? 'Guardando...' : '💾 Guardar Validación Manual'}
          </button>
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        🛡️ Escaneo de Postura de Seguridad
      </h2>
      
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
        <p className="text-sm text-blue-700">
          <strong>Detección pasiva y ética de protecciones perimetrales:</strong>
        </p>
        <ul className="text-sm text-blue-700 mt-2 ml-4 list-disc">
          <li>🛡️ Antivirus Enterprise (consolas de administración remota)</li>
          <li>🔥 Firewall Perimetral (análisis de puertos y políticas)</li>
          <li>🔐 Servidores VPN (OpenVPN, IPSec, PPTP, L2TP)</li>
          <li>🌐 WAF y CDN (Cloudflare, Akamai, AWS, Imperva)</li>
        </ul>
      </div>

      {/* Mensaje si SuperAdmin no ha seleccionado empresa */}
      {user?.role === 'superadmin' && !selectedCompanyId && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
          <p className="text-yellow-700">
            ⚠️ Selecciona una empresa arriba para ver sus targets y poder realizar escaneos.
          </p>
        </div>
      )}

      {/* Mostrar el formulario solo si hay targets */}
      {targets.length > 0 ? (
        <form onSubmit={startScan} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar Objetivo
            </label>
            <select
              value={selectedTarget}
              onChange={(e) => setSelectedTarget(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">-- Selecciona un target --</option>
              {targets.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.name} ({target.host})
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={loading || !selectedTarget}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
          >
            {loading ? 'Escaneando...' : '🚀 Iniciar Escaneo'}
          </button>
        </form>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {user?.role === 'superadmin' && !selectedCompanyId 
              ? 'Selecciona una empresa para ver sus targets'
              : 'No hay targets disponibles para escanear'}
          </p>
        </div>
      )}
    </div>
  );

  const renderScanning = () => (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Escaneo en Progreso</h2>
      
      {scanProgress && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-700">{scanProgress.current_step}</span>
            <span className="text-blue-600 font-semibold">{scanProgress.progress}%</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-blue-600 h-4 rounded-full transition-all duration-500"
              style={{ width: `${scanProgress.progress}%` }}
            ></div>
          </div>
          
          <div className="mt-4 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      )}
    </div>
  );

  const renderResults = () => {
    if (!scanResults) return null;

    return (
      <div className="space-y-6">
        {/* Score General */}
        <div className={`rounded-lg shadow-lg p-6 border-l-4 ${getScoreBgColor(scanResults.overall_score)}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Puntuación de Postura de Seguridad</h3>
              <p className="text-gray-600">Nivel: {scanResults.security_level}</p>
            </div>
            <div className={`text-6xl font-bold ${getScoreColor(scanResults.overall_score)}`}>
              {scanResults.overall_score}
              <span className="text-2xl text-gray-500">/55</span>
            </div>
          </div>
        </div>

        {/* Detalles de AV, Firewall, VPN */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h4 className="font-semibold text-gray-800 mb-2">🛡️ Antivirus</h4>
            <p className={scanResults.antivirus?.detected ? 'text-green-600' : 'text-gray-500'}>
              {scanResults.antivirus?.detected ? '✓ Detectado' : 'No detectado'}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h4 className="font-semibold text-gray-800 mb-2">🔥 Firewall</h4>
            <p className={scanResults.firewall?.detected ? 'text-green-600' : 'text-gray-500'}>
              {scanResults.firewall?.detected ? '✓ Detectado' : 'No detectado'}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h4 className="font-semibold text-gray-800 mb-2">🔐 VPN</h4>
            <p className={scanResults.vpn?.detected ? 'text-green-600' : 'text-gray-500'}>
              {scanResults.vpn?.detected ? '✓ Detectado' : 'No detectado'}
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            setScanResults(null);
            setViewMode('form');
          }}
          className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold"
        >
          Volver
        </button>
      </div>
    );
  };

  const renderHistory = () => (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        📋 Historial de Escaneos
      </h2>

      {scans.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            {user?.role === 'superadmin' && !selectedCompanyId 
              ? 'Selecciona una empresa para ver su historial'
              : 'No hay escaneos previos'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {scans.map((scan) => (
            <div
              key={scan.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition cursor-pointer"
              onClick={() => {
                setScanResults(scan.results);
                setViewMode('results');
              }}
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-semibold">{new Date(scan.created_at).toLocaleString('es-ES')}</p>
                  <p className="text-sm text-gray-500">Status: {scan.status}</p>
                </div>
                {scan.results?.overall_score !== undefined && (
                  <span className={`text-2xl font-bold ${getScoreColor(scan.results.overall_score)}`}>
                    {scan.results.overall_score}/55
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Selector de empresa para SuperAdmin */}
      {renderCompanySelector()}
      
      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
        <div className="flex space-x-4 border-b">
          <button
            onClick={() => setViewMode('form')}
            className={`px-4 py-2 font-medium ${
              viewMode === 'form' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'
            }`}
          >
            🔐 Nuevo Escaneo
          </button>
          <button
            onClick={() => setViewMode('history')}
            className={`px-4 py-2 font-medium ${
              viewMode === 'history' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'
            }`}
          >
            📋 Historial ({scans.length})
          </button>
          {user?.role === 'superadmin' && (
            <button
              onClick={() => setViewMode('manual')}
              className={`px-4 py-2 font-medium ${
                viewMode === 'manual' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'
              }`}
            >
              ✍️ Validación Manual
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {viewMode === 'form' && renderForm()}
      {viewMode === 'scanning' && renderScanning()}
      {viewMode === 'results' && renderResults()}
      {viewMode === 'history' && renderHistory()}
      {viewMode === 'manual' && renderManualValidationPanel()}
    </div>
  );
};

export default SecurityPostureScanner;
